import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { Box, ButtonGroup, IconButton, Tooltip } from "@chakra-ui/react";
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaListUl,
  FaListOl,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaAlignJustify,
} from "react-icons/fa";
import { useEffect } from "react";

const MiniRichTextEditor = ({ value, onChange, placeholder = "Enter default value..." }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      onChange(JSON.stringify(json));
    },
  });

  // Update editor when value changes externally
  useEffect(() => {
    if (editor && value !== JSON.stringify(editor.getJSON())) {
      try {
        const parsedValue = typeof value === "string" && value.trim() !== ""
          ? JSON.parse(value)
          : { type: "doc", content: [{ type: "paragraph", content: [{ type: "text", text: value || "" }] }] };
        editor.commands.setContent(parsedValue);
      } catch (e) {
        // If value is plain text (backward compatibility), set it as paragraph
        editor.commands.setContent({
          type: "doc",
          content: [{ type: "paragraph", content: value ? [{ type: "text", text: value }] : [] }],
        });
      }
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box
        borderWidth="1px"
        borderBottom="none"
        borderRadius="md"
        borderBottomRadius="none"
        p={2}
        bg="gray.50"
        display="flex"
        gap={2}
        flexWrap="wrap">
        {/* Text Formatting */}
        <ButtonGroup size="sm" isAttached variant="outline">
          <Tooltip label="Bold">
            <IconButton
              icon={<FaBold />}
              onClick={() => editor.chain().focus().toggleBold().run()}
              isActive={editor.isActive("bold")}
              bg={editor.isActive("bold") ? "blue.100" : "white"}
              aria-label="Bold"
            />
          </Tooltip>
          <Tooltip label="Italic">
            <IconButton
              icon={<FaItalic />}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              isActive={editor.isActive("italic")}
              bg={editor.isActive("italic") ? "blue.100" : "white"}
              aria-label="Italic"
            />
          </Tooltip>
          <Tooltip label="Underline">
            <IconButton
              icon={<FaUnderline />}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              isActive={editor.isActive("underline")}
              bg={editor.isActive("underline") ? "blue.100" : "white"}
              aria-label="Underline"
            />
          </Tooltip>
        </ButtonGroup>

        {/* Lists */}
        <ButtonGroup size="sm" isAttached variant="outline">
          <Tooltip label="Bullet List">
            <IconButton
              icon={<FaListUl />}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              isActive={editor.isActive("bulletList")}
              bg={editor.isActive("bulletList") ? "blue.100" : "white"}
              aria-label="Bullet List"
            />
          </Tooltip>
          <Tooltip label="Numbered List">
            <IconButton
              icon={<FaListOl />}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              isActive={editor.isActive("orderedList")}
              bg={editor.isActive("orderedList") ? "blue.100" : "white"}
              aria-label="Numbered List"
            />
          </Tooltip>
        </ButtonGroup>

        {/* Text Alignment */}
        <ButtonGroup size="sm" isAttached variant="outline">
          <Tooltip label="Align Left">
            <IconButton
              icon={<FaAlignLeft />}
              onClick={() => editor.chain().focus().setTextAlign("left").run()}
              isActive={editor.isActive({ textAlign: "left" })}
              bg={editor.isActive({ textAlign: "left" }) ? "blue.100" : "white"}
              aria-label="Align Left"
            />
          </Tooltip>
          <Tooltip label="Align Center">
            <IconButton
              icon={<FaAlignCenter />}
              onClick={() => editor.chain().focus().setTextAlign("center").run()}
              isActive={editor.isActive({ textAlign: "center" })}
              bg={editor.isActive({ textAlign: "center" }) ? "blue.100" : "white"}
              aria-label="Align Center"
            />
          </Tooltip>
          <Tooltip label="Align Right">
            <IconButton
              icon={<FaAlignRight />}
              onClick={() => editor.chain().focus().setTextAlign("right").run()}
              isActive={editor.isActive({ textAlign: "right" })}
              bg={editor.isActive({ textAlign: "right" }) ? "blue.100" : "white"}
              aria-label="Align Right"
            />
          </Tooltip>
          <Tooltip label="Justify">
            <IconButton
              icon={<FaAlignJustify />}
              onClick={() => editor.chain().focus().setTextAlign("justify").run()}
              isActive={editor.isActive({ textAlign: "justify" })}
              bg={editor.isActive({ textAlign: "justify" }) ? "blue.100" : "white"}
              aria-label="Justify"
            />
          </Tooltip>
        </ButtonGroup>
      </Box>

      {/* Editor Content */}
      <Box
        borderWidth="1px"
        borderRadius="md"
        borderTopRadius="none"
        p={3}
        minH="150px"
        bg="white"
        sx={{
          ".ProseMirror": {
            outline: "none",
            minHeight: "120px",
            "& p.is-editor-empty:first-of-type::before": {
              content: `"${placeholder}"`,
              color: "gray.400",
              float: "left",
              height: 0,
              pointerEvents: "none",
            },
          },
        }}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default MiniRichTextEditor;
